import { Grid, Link, Typography } from '@mui/material';
import { ModalName } from 'app/enums';
import { modalsReducers } from 'app/store/modal/slice';
import { MultiCurrencyConfig } from 'config/multi-currency-config';
import { useUserAccounts } from 'modules/payments/hooks/use-user-accounts';
import { useValidateUserEntry } from 'modules/payments/hooks/use-validate-user-entry';
import { RampWidgetContext } from 'modules/ramp';
import React, { FC, useCallback, useContext, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { GameActivityPaymentContext } from 'modules/game-payments/providers';
import { ActionGuard } from 'shared/components/ActionGuard';
import { LoaderButton } from 'shared/components/LoaderButton/LoaderButton';
import { PaidActionText } from './PaidActionText';
import { PayWithRewardActionText } from './PayWithRewardActionText';

interface ChildrenFunctionProps {
  isCanNotEnter: boolean;
  text: React.ReactNode | string;
  onClick: () => void;
}

interface ComponentProps {
  amount: number;
  label: string;
  tooltipText?: string;
  isLoading?: boolean;
  children: (childProps: ChildrenFunctionProps) => React.ReactNode;
  onClick: () => void;
}

export const WithPaidActionButton: FC<ComponentProps> = ({
  amount,
  label,
  tooltipText,
  isLoading = false,
  onClick: originalOnClick,
  children,
}) => {
  const dispatch = useDispatch();
  const openModal = useCallback(
    (modalName: ModalName) => {
      dispatch(modalsReducers.openModal({ name: modalName }));
    },
    [dispatch]
  );

  const { paymentConfig, platformFeePercentage, isRewardPaymentsEnabled } = useContext(
    GameActivityPaymentContext
  );

  const {
    openWidget: openRampWidget,
    isLoading: isRampLoading,
    isEnabled: isRampEnabled,
  } = useContext(RampWidgetContext);

  const { mainAccountRewardBalance: rewardBalance } = useUserAccounts();

  const formContext = useFormContext();
  const setIsRewardBalanceSelected = useCallback(
    (isRewardBalanceSelected: boolean) => {
      const setValue = formContext?.setValue;
      if (typeof setValue === 'function') {
        setValue('isRewardBalanceSelected', isRewardBalanceSelected);
      }
    },
    [formContext]
  );

  const canUseRewardBalance =
    isRewardPaymentsEnabled && (rewardBalance ?? 0) > 0 && Boolean(amount);

  const { canEnter, canEnterWithReward, needSwap, openSwapModal } = useValidateUserEntry(amount);

  const needShowSwapModal =
    paymentConfig.isPlatformFeeApplied &&
    MultiCurrencyConfig.isSwapEnabled() &&
    canEnter &&
    needSwap;

  const isCanNotEnter = paymentConfig.isPlatformFeeApplied && !canEnter;

  const text = (
    <PaidActionText
      label={label}
      tooltipLabel={tooltipText ?? label}
      entryFee={amount}
      needShowSwap={needShowSwapModal}
    />
  );

  const onRegularBalancePayClick = useCallback(() => {
    if (needShowSwapModal) {
      openSwapModal();
      return;
    }

    setIsRewardBalanceSelected(false);

    originalOnClick();
  }, [needShowSwapModal, openSwapModal, originalOnClick, setIsRewardBalanceSelected]);

  const onRewardBalancePayClick = useCallback(() => {
    setIsRewardBalanceSelected(true);

    originalOnClick();
  }, [setIsRewardBalanceSelected, originalOnClick]);

  const onBuyClick = useCallback(() => {
    if (isRampLoading) return;

    openRampWidget({
      originalAmount: amount,
      platformFeePercentage,
      currencyType: paymentConfig.currencyType,
    });
  }, [amount, isRampLoading, openRampWidget, platformFeePercentage, paymentConfig.currencyType]);

  const rampButton = useMemo(
    () => (
      <>
        {isRampLoading ? (
          <Typography sx={{ cursor: 'progress' }} component="span">
            Buy {paymentConfig.mainCurrency}
          </Typography>
        ) : (
          <Link onClick={onBuyClick}>Buy {paymentConfig.mainCurrency}</Link>
        )}{' '}
        or{' '}
      </>
    ),
    [isRampLoading, onBuyClick, paymentConfig.mainCurrency]
  );

  const cannotEnterRegularHint = useMemo(() => {
    if (canEnter) return null;

    return (
      <Typography color="error.light" mt={1} textAlign="center">
        Not enough {paymentConfig.mainCurrency} balance. Please {isRampEnabled ? rampButton : null}
        <ActionGuard>
          <Link onClick={() => openModal(ModalName.BalanceAddFunds)}>
            Transfer {paymentConfig.mainCurrency}
          </Link>
        </ActionGuard>{' '}
        to {label}.
      </Typography>
    );
  }, [canEnter, isRampEnabled, label, openModal, rampButton, paymentConfig.mainCurrency]);

  const cannotEnterRewardHint = useMemo(() => {
    if (canEnterWithReward) return null;

    return (
      <Typography color="error.light" mt={1} textAlign="center">
        Not enough reward balance to {label}.
      </Typography>
    );
  }, [canEnterWithReward, label]);

  const rewardActions = (
    <Grid item xs={6} display="flex" flexDirection="column">
      <LoaderButton
        color="primary"
        variant="contained"
        onClick={onRewardBalancePayClick}
        disabled={!canEnterWithReward}
        loading={isLoading}
      >
        <PayWithRewardActionText label={label} entryFee={amount} />
      </LoaderButton>
      {cannotEnterRewardHint}
    </Grid>
  );

  return (
    <Grid container columnSpacing={canUseRewardBalance ? 1 : 0}>
      {canUseRewardBalance ? rewardActions : null}
      <Grid item xs={canUseRewardBalance ? 6 : 12} display="flex" flexDirection="column">
        {children({ isCanNotEnter, text, onClick: onRegularBalancePayClick })}
        {cannotEnterRegularHint}
      </Grid>
    </Grid>
  );
};
